/**
 * SessionBootScreen
 *
 * Full-page loading screen shown while the cloud session backend is booting.
 * Displays phase-based status messages and handles error/retry.
 */
import React, { useEffect, useState } from 'react';
import { formatErrorMessage } from '../../utils/errorUtils';
import TrexRunner from './trex/TrexRunner';
import './SessionBootScreen.css';

const PHASE_MESSAGES = {
    PROVISIONING: 'Launching your environment',
    STARTING: 'Starting services',
    SETTING_UP: 'Setting up workspace',
};

function SessionBootScreen({ sessionStatus, error, onRetry, pendingAction }) {
    const [elapsedSec, setElapsedSec] = useState(0);

    // Elapsed time counter
    useEffect(() => {
        if (error) return;
        const id = setInterval(() => {
            setElapsedSec(prev => prev + 1);
        }, 1000);
        return () => clearInterval(id);
    }, [error]);

    // Reset elapsed time only when the component mounts fresh (new boot cycle).
    // sessionStatus changes (PROVISIONING -> STARTING) should NOT reset the timer.

    const getPhaseMessage = () => {
        if (sessionStatus === 'RUNNING') return PHASE_MESSAGES.SETTING_UP;
        return PHASE_MESSAGES[sessionStatus] || PHASE_MESSAGES.PROVISIONING;
    };

    const getSecondaryMessage = () => {
        if (!pendingAction) return null;
        if (pendingAction.type === 'new') {
            return pendingAction.projectCreated
                ? 'Project created'
                : 'Creating project';
        }
        if (pendingAction.type === 'open') {
            return 'Preparing project';
        }
        return null;
    };

    const getTimeHint = () => {
        if (elapsedSec < 30) return 'First launch takes about 90 seconds \u2014 next time it\u2019ll be much faster';
        if (elapsedSec < 45) return `${elapsedSec}s elapsed \u2014 warming up`;
        if (elapsedSec < 90) return `${elapsedSec}s elapsed \u2014 almost there`;
        return `${elapsedSec}s elapsed \u2014 still working`;
    };

    if (error) {
        return (
            <div className="session-boot-screen">
                <div className="session-boot-content">
                    <div className="session-boot-error-icon">!</div>
                    <h2 className="session-boot-title">Something went wrong</h2>
                    <p className="session-boot-error-message">{formatErrorMessage(error)}</p>
                    {onRetry && (
                        <button className="session-boot-retry-btn" onClick={onRetry}>
                            Try Again
                        </button>
                    )}
                </div>
            </div>
        );
    }

    const secondaryMessage = getSecondaryMessage();

    return (
        <div className="session-boot-screen">
            <div className="session-boot-content">
                <div className="session-boot-spinner" />
                <h2 className="session-boot-title">
                    {getPhaseMessage()}
                </h2>
                {secondaryMessage && (
                    <div className="session-boot-secondary">
                        {pendingAction?.projectCreated && (
                            <span className="session-boot-check">\u2713</span>
                        )}
                        <span>{secondaryMessage}</span>
                    </div>
                )}
                <p className="session-boot-hint">{getTimeHint()}</p>
                <div className="session-boot-phases">
                    <PhaseStep
                        label="Launch container"
                        done={sessionStatus === 'STARTING' || sessionStatus === 'RUNNING'}
                        active={sessionStatus === 'PROVISIONING'}
                    />
                    <PhaseStep
                        label="Start application"
                        done={sessionStatus === 'RUNNING'}
                        active={sessionStatus === 'STARTING'}
                    />
                    <PhaseStep
                        label="Ready"
                        done={false}
                        active={sessionStatus === 'RUNNING'}
                    />
                </div>
                <TrexRunner />
            </div>
        </div>
    );
}

function PhaseStep({ label, done, active }) {
    const className = `session-boot-phase ${done ? 'done' : ''} ${active ? 'active' : ''}`;
    return (
        <div className={className}>
            <div className="session-boot-phase-dot" />
            <span>{label}</span>
        </div>
    );
}

export default SessionBootScreen;
